import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DecimalInput } from "@/components/ui/decimal-input";
import { SourceOfTruthBadge } from "@/components/ledger/SourceOfTruthBadge";
import { usePeople } from "@/lib/people-store";
import { isKarigarParty } from "@/lib/party-types";
import { useWorkerGoldBook, type WorkerGoldBookEntry } from "@/lib/worker-gold-book-store";
import { useStock } from "@/lib/stock-store";
import { useLedger } from "@/lib/ledger-store";
import { formatWeight, mgToGrams, gramsToMg } from "@/lib/gold";
import { paiseToRupees, rupeesToPaise } from "@/lib/billing-store";
import { toast } from "sonner";
import {
  PackageCheck,
  ArrowDownLeft,
  ArrowRight,
  Scale,
  Search,
  CheckCircle2,
  AlertTriangle,
  FileText,
  User,
  History,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/workshop/jama")({
  head: () => ({ meta: [{ title: "Jama Return (Material & Finished In) · AVS ERP" }] }),
  component: JamaReturnPage,
});

export function JamaReturnPage() {
  const searchParams = useRouterState({
    select: (s) => (s.location.search as Record<string, string> | undefined) ?? {},
  });

  const people = usePeople((s) => s.people);
  const karigars = useMemo(
    () => people.filter((p) => isKarigarParty(p) && p.active),
    [people],
  );

  const { entries, addEntry, getOpenNaveIssues, refresh } = useWorkerGoldBook();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Selected Karigar
  const [selectedKarigarId, setSelectedKarigarId] = useState<string>(
    searchParams.karigarId || "",
  );

  // Selected parent Nave issue
  const [selectedNaveId, setSelectedNaveId] = useState<string>(
    searchParams.naveId || "",
  );

  // Form Fields
  const [returnKind, setReturnKind] = useState<
    "finished_good" | "semi_finished" | "scrap" | "filings"
  >("finished_good");
  const [itemName, setItemName] = useState("");
  const [grossGrams, setGrossGrams] = useState("");
  const [lessGrams, setLessGrams] = useState("0");
  const [purityPermille, setPurityPermille] = useState("916");
  const [scrapGrossGrams, setScrapGrossGrams] = useState("0");
  const [scrapPurity, setScrapPurity] = useState("916");
  const [filingsGrams, setFilingsGrams] = useState("0");
  const [allowedWastagePct, setAllowedWastagePct] = useState("1.5");
  const [labourRateINR, setLabourRateINR] = useState("450");
  const [labourMode, setLabourMode] = useState<"per_gram" | "fixed">("per_gram");
  const [qcStatus, setQcStatus] = useState<"passed" | "pending" | "rework">("passed");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter open Nave issues
  const openNaveIssues = useMemo(() => {
    return getOpenNaveIssues(selectedKarigarId || undefined);
  }, [getOpenNaveIssues, selectedKarigarId]);

  const activeNave = useMemo(() => {
    if (!selectedNaveId) return null;
    return entries.find((e) => e.id === selectedNaveId) ?? null;
  }, [entries, selectedNaveId]);

  // Auto-fill purity when Nave issue selected
  useEffect(() => {
    if (activeNave) {
      setPurityPermille(String(activeNave.purity || 916));
      setScrapPurity(String(activeNave.purity || 916));
      if (!itemName && activeNave.particulars) {
        setItemName(activeNave.particulars);
      }
    }
  }, [activeNave, itemName]);

  // Calculations
  const calc = useMemo(() => {
    const gross = parseFloat(grossGrams) || 0;
    const less = parseFloat(lessGrams) || 0;
    const net = Math.max(0, gross - less);
    const purity = parseInt(purityPermille, 10) || 0;
    const fine995 = purity > 0 ? Math.round((gramsToMg(net) * purity) / 995) : 0;

    const scrapGross = parseFloat(scrapGrossGrams) || 0;
    const scrapPur = parseInt(scrapPurity, 10) || 0;
    const scrapFine995 = scrapPur > 0 ? Math.round((gramsToMg(scrapGross) * scrapPur) / 995) : 0;

    const filings = parseFloat(filingsGrams) || 0;
    const filingsFine995 = purity > 0 ? Math.round((gramsToMg(filings) * purity) / 995) : 0;

    const issuedGrossG = activeNave ? (activeNave.grossMg || 0) / 1000 : 0;
    const issuedFine995G = activeNave ? (activeNave.fine995Mg || activeNave.fineMg || 0) / 1000 : 0;

    const returnedTotalG = net + scrapGross + filings;
    const actualLossG = Math.max(0, issuedGrossG - returnedTotalG);
    const allowedPct = parseFloat(allowedWastagePct) || 0;
    const allowedLossG = (net * allowedPct) / 100;
    const varianceG = actualLossG - allowedLossG;
    const isOverLoss = varianceG > 0.002; // tolerance 2mg

    const rate = parseFloat(labourRateINR) || 0;
    const labourPaise =
      labourMode === "per_gram" ? rupeesToPaise(net * rate) : rupeesToPaise(rate);

    return {
      netG: net,
      fine995Mg: fine995,
      fine995G: fine995 / 1000,
      scrapGrossG: scrapGross,
      scrapFine995Mg: scrapFine995,
      scrapFine995G: scrapFine995 / 1000,
      filingsG: filings,
      filingsFine995Mg: filingsFine995,
      actualLossG,
      allowedLossG,
      varianceG,
      isOverLoss,
      labourPaise,
    };
  }, [
    grossGrams,
    lessGrams,
    purityPermille,
    scrapGrossGrams,
    scrapPurity,
    filingsGrams,
    allowedWastagePct,
    activeNave,
    labourRateINR,
    labourMode,
  ]);

  // Submit Jama
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedKarigarId) {
      toast.error("Please select a karigar.");
      return;
    }
    const karigar = karigars.find((k) => k.id === selectedKarigarId);
    if (!karigar) {
      toast.error("Invalid karigar.");
      return;
    }
    if (calc.netG <= 0 && calc.scrapGrossG <= 0 && calc.filingsG <= 0) {
      toast.error("Please enter at least finished weight, scrap, or filings.");
      return;
    }

    setSubmitting(true);
    try {
      const netMg = gramsToMg(calc.netG);
      const grossMg = gramsToMg(parseFloat(grossGrams) || 0);
      const lessMg = gramsToMg(parseFloat(lessGrams) || 0);
      const purity = parseInt(purityPermille, 10) || 916;

      const entry = await addEntry({
        workerId: selectedKarigarId,
        workerName: karigar.fullName,
        particulars: itemName.trim() || `Jama: ${returnKind.replace("_", " ")}`,
        grossMg,
        lessMg,
        netMg,
        quantity: 1,
        purity,
        fineMg: calc.fine995Mg,
        fine995Mg: calc.fine995Mg,
        type: "return",
        returnItemKind: returnKind,
        naveEntryId: selectedNaveId || undefined,
        naveEntryNo: activeNave?.entryNo,
        dhadiGroupId: activeNave?.dhadiGroupId,
        dhadiGroupName: activeNave?.dhadiGroupName,
        orderId: activeNave?.orderId,
        orderNo: activeNave?.orderNo,
        givenBy: karigar.fullName,
        receivedBy: "Showroom Vault / Store Staff",
        labourCashPaise: calc.labourPaise,
        labourRatePaise: rupeesToPaise(parseFloat(labourRateINR) || 0),
        recoveredFilingsMg: gramsToMg(calc.filingsG),
        qcStatus,
        notes: notes.trim(),
        overLossGrossMg: calc.isOverLoss ? gramsToMg(calc.varianceG) : 0,
        approvalStatus: calc.isOverLoss ? "pending_approval" : "approved",
      });

      // Post scrap return line if scrap returned
      if (calc.scrapGrossG > 0) {
        await addEntry({
          workerId: selectedKarigarId,
          workerName: karigar.fullName,
          particulars: `Scrap return for ${entry.entryNo}`,
          grossMg: gramsToMg(calc.scrapGrossG),
          lessMg: 0,
          netMg: gramsToMg(calc.scrapGrossG),
          quantity: 1,
          purity: parseInt(scrapPurity, 10) || purity,
          fineMg: calc.scrapFine995Mg,
          fine995Mg: calc.scrapFine995Mg,
          type: "return",
          returnItemKind: "scrap",
          naveEntryId: selectedNaveId || undefined,
          naveEntryNo: activeNave?.entryNo,
          givenBy: karigar.fullName,
          receivedBy: "Scrap Vault",
          notes: `Scrap inward against ${entry.entryNo}`,
        });
      }

      toast.success(`Jama voucher ${entry.entryNo} posted successfully!`);
      // Reset form
      setGrossGrams("");
      setLessGrams("0");
      setScrapGrossGrams("0");
      setFilingsGrams("0");
      setItemName("");
      setNotes("");
      setSelectedNaveId("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record Jama return.");
    } finally {
      setSubmitting(false);
    }
  }

  // Recent Jama returns
  const recentJamaReturns = useMemo(() => {
    return entries
      .filter((e) => e.type === "return")
      .slice(0, 15);
  }, [entries]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Jama Return (Material & Finished In)"
        subtitle="First-class material intake from artisans: finished ornaments, semi-finished goods, scrap, and filings linked to original Nave issues."
        actions={<SourceOfTruthBadge variant="ledger" />}
      />

      {/* Top Workflow Context & Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-border bg-card">
        <div className="flex items-center gap-2">
          <ArrowDownLeft className="h-5 w-5 text-emerald-500" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">SRM Manufacturing Inward (Jama)</h2>
            <p className="text-xs text-muted-foreground">
              Direct custody relief, automatic scrap & ready stock inward, 995 fine gold reconciliation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/gold-book" search={{ mode: "issue" }}>
              Nave Issue Form
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/bench-custody">
              Bench Custody
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/reconciliation">
              Reconciliation
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Form: Jama Return Entry */}
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-2 rounded-xl border border-border bg-card p-5 space-y-5 shadow-xs"
        >
          <div className="border-b border-border pb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4 text-emerald-500" />
              Record Inward Return (Jama)
            </h3>
            <span className="text-xs text-muted-foreground">Linked to Nave Issue</span>
          </div>

          {/* Karigar & Nave Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">1. Select Karigar (Artisan) *</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                value={selectedKarigarId}
                onChange={(e) => {
                  setSelectedKarigarId(e.target.value);
                  setSelectedNaveId("");
                }}
                required
              >
                <option value="">-- Choose Artisan --</option>
                {karigars.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.fullName} ({k.phone || "No phone"})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">2. Select Parent Nave Issue *</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                value={selectedNaveId}
                onChange={(e) => setSelectedNaveId(e.target.value)}
              >
                <option value="">
                  {openNaveIssues.length === 0
                    ? selectedKarigarId
                      ? "No open Nave issues found"
                      : "-- Select Karigar first --"
                    : "-- Select Linked Nave Issue --"}
                </option>
                {openNaveIssues.map((nave) => (
                  <option key={nave.id} value={nave.id}>
                    {nave.entryNo} · {nave.particulars} · Issued: {formatWeight(nave.grossMg)} ({nave.purity}‰) · {nave.date}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Nave Details Display */}
          {activeNave && (
            <div className="rounded-lg bg-muted/40 p-3 text-xs border border-border flex flex-wrap gap-4 items-center justify-between">
              <div>
                <span className="text-muted-foreground">Original Issue:</span>{" "}
                <span className="font-semibold">{activeNave.entryNo}</span> ({activeNave.date})
              </div>
              <div>
                <span className="text-muted-foreground">Issued Gross:</span>{" "}
                <span className="font-mono font-semibold">{formatWeight(activeNave.grossMg)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Purity:</span>{" "}
                <span className="font-mono font-semibold">{activeNave.purity}‰</span>
              </div>
              <div>
                <span className="text-muted-foreground">Fine @995:</span>{" "}
                <span className="font-mono font-semibold text-amber-500">
                  {formatWeight(activeNave.fine995Mg || activeNave.fineMg)}
                </span>
              </div>
            </div>
          )}

          {/* Return Type & Item Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Return Item Type</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                value={returnKind}
                onChange={(e) => setReturnKind(e.target.value as any)}
              >
                <option value="finished_good">Finished Ornament (Ready for QC)</option>
                <option value="semi_finished">Semi-Finished Component / WIP</option>
                <option value="scrap">Clean Scrap / Runner</option>
                <option value="filings">Bench Filings / Dust</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Item Description / Particulars</Label>
              <Input
                placeholder="e.g. 22K Ladies Ring Floral / Scrap return"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
            </div>
          </div>

          {/* Weights & Purity Grid */}
          <div className="p-4 rounded-lg border border-border bg-muted/20 space-y-4">
            <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
              Finished / Returned Weights
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Gross Wt (g) *</Label>
                <DecimalInput
                  placeholder="0.000"
                  value={grossGrams}
                  onChange={setGrossGrams}
                  className="font-mono"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Less/Stones Wt (g)</Label>
                <DecimalInput
                  placeholder="0.000"
                  value={lessGrams}
                  onChange={setLessGrams}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Net Wt (g)</Label>
                <Input
                  value={calc.netG.toFixed(3)}
                  readOnly
                  className="font-mono bg-muted font-semibold"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Touch Purity (‰)</Label>
                <Input
                  placeholder="916"
                  value={purityPermille}
                  onChange={(e) => setPurityPermille(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>

            {/* Scrap & Filings */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-border/60">
              <div className="space-y-1">
                <Label className="text-xs">Scrap Gross Wt (g)</Label>
                <DecimalInput
                  placeholder="0.000"
                  value={scrapGrossGrams}
                  onChange={setScrapGrossGrams}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Scrap Purity (‰)</Label>
                <Input
                  placeholder="916"
                  value={scrapPurity}
                  onChange={(e) => setScrapPurity(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Filings / Dust Wt (g)</Label>
                <DecimalInput
                  placeholder="0.000"
                  value={filingsGrams}
                  onChange={setFilingsGrams}
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          {/* Loss & Majuri Reconciliation */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg border border-border bg-background">
            <div className="space-y-1">
              <Label className="text-xs">Allowed Wastage %</Label>
              <Input
                placeholder="1.5"
                value={allowedWastagePct}
                onChange={(e) => setAllowedWastagePct(e.target.value)}
                className="font-mono"
              />
              <span className="text-[11px] text-muted-foreground">
                Limit: {calc.allowedLossG.toFixed(3)} g
              </span>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Actual Loss</Label>
              <div className="font-mono text-sm font-semibold pt-1.5">
                {calc.actualLossG.toFixed(3)} g
              </div>
              <span className="text-[11px] text-muted-foreground">
                Issued − (Finished + Scrap + Filings)
              </span>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Variance / Over-loss</Label>
              <div
                className={`font-mono text-sm font-semibold pt-1.5 ${
                  calc.isOverLoss ? "text-rose-500" : "text-emerald-500"
                }`}
              >
                {calc.varianceG > 0 ? `+${calc.varianceG.toFixed(3)} g (Excess)` : `${calc.varianceG.toFixed(3)} g (OK)`}
              </div>
              {calc.isOverLoss && (
                <span className="text-[11px] text-rose-500 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Requires Approval
                </span>
              )}
            </div>
          </div>

          {/* Majuri & QC */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Labour Mode</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={labourMode}
                onChange={(e) => setLabourMode(e.target.value as any)}
              >
                <option value="per_gram">Per Gram (₹ / g)</option>
                <option value="fixed">Fixed Lump-Sum (₹ / pc)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Labour Rate (₹)</Label>
              <Input
                value={labourRateINR}
                onChange={(e) => setLabourRateINR(e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Initial QC Status</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={qcStatus}
                onChange={(e) => setQcStatus(e.target.value as any)}
              >
                <option value="passed">QC Passed (Ready for HUID)</option>
                <option value="pending">Inspection Pending</option>
                <option value="rework">Rework Required</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Notes / Remarks</Label>
            <Input
              placeholder="Design polish remarks, stamp mark, artisan memo..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="pt-2 flex justify-end gap-3">
            <Button
              type="submit"
              variant="default"
              className="px-6 cursor-pointer bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              disabled={submitting}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {submitting ? "Posting Jama..." : "Post Jama Voucher"}
            </Button>
          </div>
        </form>

        {/* Right Panel: Live Calculation Summary & Recent Inwards */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4 shadow-xs">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Scale className="h-4 w-4 text-amber-500" />
              Live Inward Summary (@995)
            </h3>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Finished Net Weight:</span>
                <span className="font-mono font-medium">{calc.netG.toFixed(3)} g</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Finished Fine Gold @995:</span>
                <span className="font-mono font-semibold text-amber-500">
                  {calc.fine995G.toFixed(3)} g
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Scrap Fine Gold @995:</span>
                <span className="font-mono font-medium">{calc.scrapFine995G.toFixed(3)} g</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Total Metal Credited:</span>
                <span className="font-mono font-semibold">
                  {(calc.fine995G + calc.scrapFine995G).toFixed(3)} g @995
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border/60">
                <span className="text-muted-foreground">Labour (Majuri) Payable:</span>
                <span className="font-mono font-semibold text-emerald-500">
                  {paiseToRupees(calc.labourPaise)}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Jama Inwards */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3 shadow-xs">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <History className="h-4 w-4 text-primary" />
              Recent Jama Returns
            </h3>

            {recentJamaReturns.length === 0 ? (
              <p className="text-xs text-muted-foreground">No recent returns recorded.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {recentJamaReturns.map((r) => (
                  <div
                    key={r.id}
                    className="p-2.5 rounded-md border border-border/60 bg-muted/20 text-xs flex justify-between items-center"
                  >
                    <div>
                      <div className="font-semibold text-foreground">{r.entryNo}</div>
                      <div className="text-muted-foreground">
                        {r.workerName} · {r.particulars}
                      </div>
                    </div>
                    <div className="text-right font-mono">
                      <div>{formatWeight(r.grossMg)}</div>
                      <div className="text-amber-500 text-[11px]">
                        {formatWeight(r.fine995Mg || r.fineMg)} @995
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
